import React from 'react';
import moment from 'moment-jalaali';
import { upperFirst } from 'lodash';
import { endOfDate, startOfDate } from 'utils/date';

import Tabs from 'components/Tabs';
import ButtonGroup from 'components/ButtonGroup';
import BaseDatePicker, { ILocal } from 'components/Chart/BaseDatePicker';
import { DateSelectorContainer } from './styles';
import { TimeScale } from 'store/survey/surveyTypes';
import i18n, { getCalendarLocal, getLangBaseOnLocal } from '../../../i18n';

moment.loadPersian({ usePersianDigits: true, dialect: 'persian-modern' });

interface IDateSelectorProps {
  compactView?: boolean;
  showBaseDate?: boolean;
  onChangeDate: (
    splitMonths: number,
    startDate: number,
    toDate: number,
    timeLength: number
  ) => void;
  local: ILocal;
}

interface IDateSelectorState {
  baseDate: Date;
  timeLength: string;
  selectedDateType: TimeScale;
  showCustomDateModal: boolean;
}

const getSplitMonths: { [key: string]: number } = {
  month: 1,
  season: 3,
  twoSeason: 6,
  year: 12,
};

class DateSelector extends React.Component<
  IDateSelectorProps,
  IDateSelectorState
> {
  monthItems = {
    1: i18n.t('oneMonth', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    3: i18n.t('threeMonths', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    6: i18n.t('sixMonths', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    12: i18n.t('twelveMonths', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
  };

  seasonItems = {
    1: i18n.t('oneSeason', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    2: i18n.t('twoSeason', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    4: i18n.t('fourSeason', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    8: i18n.t('eightSeason', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
  };

  twoSeasonNames = {
    1: i18n.t('oneTwoSeason', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    2: i18n.t('twoTwoSeason', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    4: i18n.t('fourTwoSeason', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    8: i18n.t('eightTwoSeason', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
  };

  yearItems = {
    1: i18n.t('oneYear', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    2: i18n.t('twoYear', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    3: i18n.t('threeYear', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
    5: i18n.t('fiveYear', {
      ns: 'taskManager',
      lng: getLangBaseOnLocal(this.props.local),
    }),
  };

  state: IDateSelectorState = {
    baseDate: (this.props.local === 'fa_IR@calendar=persian'
      ? moment()
      : moment().locale('en')
    )
      .endOf('day')
      .toDate(),
    timeLength: '1',
    selectedDateType: 'month',
    showCustomDateModal: false,
  };

  calculateDate = (length: string, baseDate?: Date): void => {
    this.setState({ timeLength: length });
    const local = this.props.local || getCalendarLocal();

    const timeLength = Number(length);
    const { selectedDateType } = this.state;

    const getBaseDate = baseDate ? baseDate : this.state.baseDate;

    const toDate = endOfDate(
      getBaseDate,
      selectedDateType,
      true,
      getLangBaseOnLocal(local)
    );

    let subtractedDate =
      local === 'fa_IR@calendar=persian'
        ? moment(toDate)
        : moment(toDate).locale('en');

    const monthFormat = local === 'fa_IR@calendar=persian' ? 'jMonth' : 'month';

    if (selectedDateType === 'month' || selectedDateType === 'year') {
      const scale =
        local === 'fa_IR@calendar=persian'
          ? `j${upperFirst(selectedDateType)}`
          : `${upperFirst(selectedDateType)}`;
      subtractedDate = subtractedDate.subtract(
        timeLength - 1,
        scale as moment.unitOfTime.DurationConstructor
      );
    } else if (selectedDateType === 'season') {
      subtractedDate = subtractedDate.subtract(
        (timeLength - 1) * 3,
        monthFormat as moment.unitOfTime.DurationConstructor
      );
    } else if (selectedDateType === 'twoSeason') {
      subtractedDate = subtractedDate.subtract(
        (timeLength - 1) * 6,
        monthFormat as moment.unitOfTime.DurationConstructor
      );
    }

    const startDate = startOfDate(
      subtractedDate.toDate(),
      selectedDateType,
      true,
      getLangBaseOnLocal(local)
    );

    const splitMonths = getSplitMonths[selectedDateType];

    this.props.onChangeDate(splitMonths, startDate, toDate, timeLength);
  };

  handleChangeSelectedDateType = (selectedDateType: string | number) => {
    this.setState({ selectedDateType: selectedDateType as TimeScale }, () => {
      this.calculateDate(this.state.timeLength);
    });
  };

  handleChangeBaseDate = (baseDate: Date) => {
    this.setState({ baseDate });
    this.calculateDate(this.state.timeLength, baseDate);
  };

  render() {
    const { timeLength } = this.state;
    const { showBaseDate } = this.props;
    const monthValues = (
      <ButtonGroup
        items={this.monthItems}
        selected={timeLength}
        onSelect={this.calculateDate}
      />
    );
    const seasonValues = (
      <ButtonGroup
        items={this.seasonItems}
        selected={timeLength}
        onSelect={this.calculateDate}
      />
    );
    const twoSeasonValues = (
      <ButtonGroup
        items={this.twoSeasonNames}
        selected={timeLength}
        onSelect={this.calculateDate}
      />
    );
    const yearValues = (
      <ButtonGroup
        items={this.yearItems}
        selected={timeLength}
        onSelect={this.calculateDate}
      />
    );
    const tabItems = {
      month: {
        title: i18n.t('monthly', {
          ns: 'taskManager',
          lng: getLangBaseOnLocal(this.props.local),
        }),
        content: monthValues,
      },
      season: {
        title: i18n.t('seasonal', {
          ns: 'taskManager',
          lng: getLangBaseOnLocal(this.props.local),
        }),
        content: seasonValues,
      },
      twoSeason: {
        title: i18n.t('twoSeasons', {
          ns: 'taskManager',
          lng: getLangBaseOnLocal(this.props.local),
        }),
        content: twoSeasonValues,
      },
      year: {
        title: i18n.t('yearly', {
          ns: 'taskManager',
          lng: getLangBaseOnLocal(this.props.local),
        }),
        content: yearValues,
      },
    };
    return (
      <DateSelectorContainer compactView={this.props.compactView}>
        {showBaseDate && (
          <BaseDatePicker
            width="20rem"
            baseDate={this.state.baseDate}
            onChangeBaseDate={this.handleChangeBaseDate}
            sendDateOnTabChange={true}
            local={this.props.local}
          />
        )}
        <Tabs
          tabItems={tabItems}
          activeTab={this.state.selectedDateType}
          wrapperStyle={
            this.props.compactView
              ? { margin: '0 0 1rem 0', width: '20rem' }
              : { margin: '1.4rem 1rem 0 0', width: '20rem' }
          }
          onChangeActiveTab={this.handleChangeSelectedDateType}
        />
      </DateSelectorContainer>
    );
  }
}

export default DateSelector;
